import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { ProvenanceChip, type Origin } from '@/components/ui/provenance-chip';
import { IssueSeverityChip, IssueStatusChip } from '@/components/issues/issue-severity-chip';
import { useRegisterListQuery, type RegisterEntry } from '@/lib/api/registers';
import { formatDate, titleCase } from '@/lib/utils/format';

/**
 * Issues screen (T-0101 / E04-F01).
 *
 * Issues are register_entries with `register_type='issue'` per the design
 * (RAID framework, issue slice). The screen renders the same tabular
 * primitive (`<DataGrid>`) the audit screen uses; per the ticket §6
 * "Build a shared <DataGrid>".
 *
 * Why not Yjs-collab here: live collab (T-0032) is currently wired only to
 * the risk register (`contract_compliance_baseline` default). Issues live
 * in a separate slice and can layer collab later; for v1 they are
 * read + filter only. A "New issue" affordance triggers the generic
 * create modal (T-0117) via `data-create="issue"` per the prototype.
 *
 * The issue-specific fields (severity, status, owner) come from each
 * register entry's `payload` JSON. The page renders permissively — if a
 * payload doesn't include a field we render an em-dash.
 */

export const Route = createFileRoute('/engagements/$engagementId/issues')({
  component: IssuesTab,
});

const ISSUE_REGISTER_TYPE = 'issue';

function IssuesTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/issues' });
  const [registerType, setRegisterType] = useState(ISSUE_REGISTER_TYPE);
  const [typeDraft, setTypeDraft] = useState(ISSUE_REGISTER_TYPE);
  const list = useRegisterListQuery(engagementId, registerType);

  const columns = useMemo<ReadonlyArray<DataGridColumn<RegisterEntry>>>(
    () => [
      {
        key: 'id',
        header: 'ID',
        cell: (row) => <span className="font-mono text-meta">{row.id.slice(0, 8)}…</span>,
        headerClassName: 'w-[120px]',
      },
      {
        key: 'title',
        header: 'Title',
        cell: (row) => {
          const title = extractPayloadString(row.payload, 'title') ?? '(untitled)';
          const desc = extractPayloadString(row.payload, 'description');
          return (
            <div className="space-y-1">
              <div className="text-body-sm font-medium text-ink-900">{title}</div>
              {desc ? <div className="text-meta text-ink-500">{desc}</div> : null}
            </div>
          );
        },
      },
      {
        key: 'severity',
        header: 'Severity',
        cell: (row) => {
          const sev = extractPayloadString(row.payload, 'severity');
          if (!sev) return <span className="text-ink-300">—</span>;
          return <IssueSeverityChip severity={sev} />;
        },
        headerClassName: 'w-[110px]',
      },
      {
        key: 'owner',
        header: 'Owner',
        cell: (row) => {
          const owner = extractPayloadString(row.payload, 'owner');
          return <span className="text-body-sm text-ink-700">{owner ?? '—'}</span>;
        },
        headerClassName: 'w-[160px]',
      },
      {
        key: 'status',
        header: 'Status',
        cell: (row) => <IssueStatusChip status={row.status} />,
        headerClassName: 'w-[140px]',
      },
      {
        key: 'origin',
        header: 'Origin',
        cell: (row) => <ProvenanceChip origin={mapDbOriginToUi(row.origin)} />,
        headerClassName: 'w-[140px]',
      },
      {
        key: 'updated_at',
        header: 'Updated',
        cell: (row) => (
          <span className="font-mono text-meta text-ink-500">{formatDate(row.updated_at)}</span>
        ),
        headerClassName: 'w-[110px]',
      },
    ],
    [],
  );

  const rows = list.data?.entries ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Issue register</h1>
          <p className="text-meta text-ink-500">
            Severity, age, owner, resolution. Same grid primitive as the risk register — the
            framework default for issues is RAID. Each row shows where it came from.
          </p>
        </div>
        <Button
          type="button"
          data-create="issue"
          size="sm"
          className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New issue
        </Button>
      </div>

      <div className="rounded-card border border-ink-100 bg-paper-50 p-3">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setRegisterType(typeDraft.trim() || ISSUE_REGISTER_TYPE);
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="issue-register-type" className="text-meta text-ink-500">
              Register slice
            </Label>
            <Input
              id="issue-register-type"
              value={typeDraft}
              onChange={(e) => setTypeDraft(e.target.value)}
              className="w-56 font-mono text-meta"
              placeholder="issue"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Apply
          </Button>
          <p className="text-meta text-ink-500">
            Default is <code className="font-mono">{ISSUE_REGISTER_TYPE}</code>. Swap to view
            another slice of the engagement&apos;s register entries.
          </p>
        </form>
      </div>

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading issues…
        </div>
      ) : list.error ? (
        <ErrorMessage error={list.error} />
      ) : (
        <DataGrid<RegisterEntry>
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          caption={`Issues (${rows.length})`}
          emptyState={
            <EmptyState
              title="No issues"
              description='No issues yet. Add the first one with "New issue".'
            />
          }
        />
      )}
    </div>
  );
}

/**
 * Permissive payload field extractor. Issues' `payload` is a JSON column on
 * `register_entries`; the AI extracts the fields and rows can carry
 * different shapes across engagements until firm-wide schemas converge.
 * Returns undefined for absent or non-string fields.
 */
function extractPayloadString(payload: unknown, key: string): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const v = (payload as Record<string, unknown>)[key];
  if (typeof v === 'string' && v.length > 0) return v;
  return undefined;
}

/**
 * Map the DB-side `register_entries.origin` enum to the UI-facing
 * provenance vocabulary the chip primitive expects (see provenance-chip.tsx
 * head-of-file comment). Unknown values fall back to `manual` so the chip
 * always renders.
 */
function mapDbOriginToUi(dbOrigin: string): Origin {
  switch (dbOrigin) {
    case 'ai_extracted_confirmed_as_is':
      return 'ai_confirmed';
    case 'ai_extracted_edited_by_consultant':
      return 'consultant_edited';
    case 'manually_authored':
      return 'manual';
    case 'ai_proposed':
      return 'ai_proposed';
    default:
      return 'manual';
  }
}

void titleCase;

export { IssuesTab };
